<template>
  <!-- Outer container with margins -->
  <div class="ml-12 mr-12 my-2 qa-table-container">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 15px; background: linear-gradient(135deg, #002060 0%, #001547 100%);">
          <h2 class="text-white mb-2">Law Questions</h2>
          <p class="text-white text-opacity-80 mb-0">
            Manage and respond to legal questions and inquiries
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <!-- Search input -->
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="search"
          label="Search Questions"
          clearable
          class="elevation-0"
          @input="debouncedGetQuestions"
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          append-inner-icon="mdi-magnify"
          variant="solo"
        />
      </v-col>

      <!-- Category filter -->
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="categoryFilter"
          :items="categoryOptions"
          label="Filter by Category"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedGetQuestions"
        />
      </v-col>

      <!-- Assigned To filter (replaces Status filter) -->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="assignedFilter"
          :items="assignedFilterOptions"
          item-title="title"
          item-value="value"
          label="Filter by Assignee"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          :no-data-text="'No matches'"
          :filter="(item, queryText) => (item?.title || '').toLowerCase().includes((queryText || '').toLowerCase())"
        />
      </v-col>
    </v-row>

    <!-- Data table for QA -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          id="qa-table"
          :headers="selectedQaType === 'completed' ? completedHeaders : headers"
          :items="filteredQuestions"
          :items-per-page="10"
          :loading="loading"
          class="custom-header elevation-3"
          style="border-radius: 17px;"
          loading-text="Loading... Please wait"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
          item-key="id"
        >
          <template #item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
            >
              {{ item.status || 'pending' }}
            </v-chip>
          </template>

          <template #item.assignedTo="{ item }">
            <!-- Autocomplete for assigning QA users - only for Admin, Executive/Manager, Employee -->
            <v-autocomplete
              v-if="isAdmin || isManager || isEmployee"
              v-model="rowAssigneeMap[item.id]"
              :items="qaUserOptions"
              item-title="fullName"
              item-value="id"
              density="compact"
              variant="outlined"
              hide-details
              clearable
              :loading="assigningId === item.id"
              return-object
              style="width: 300px; margin: 0 auto; display: block;"
              :menu-props="{ maxHeight: 200 }"
              placeholder="Assign to"
              @update:model-value="(val) => handleAssignedToChange(item, val)"
              :filter="customUserFilter"
              :no-data-text="'No QA Agents found'"
            >
              <!-- Only show the name line -->
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #title>
                    {{ item.raw.fullName }}
                  </template>
                </v-list-item>
              </template>

              <!-- How the selection chip renders -->
              <template #selection="{ item }">
                <v-chip size="small" color="primary" class="text-white" variant="flat">
                  {{ item.raw.fullName }}
                </v-chip>
              </template>
            </v-autocomplete>

            <!-- Display for QA Agents (no edit) -->
            <span v-else>
              {{ getAssignedToDisplayValue(item) }}
            </span>
          </template>

          <template #item.question="{ item }">
            <span :title="item.question">
              {{ item.question && item.question.length > 40 ? item.question.substring(0, 40) + '...' : item.question }}
            </span>
          </template>

          <template #item.action="{ item }">
            <!-- View/Answer button -->
            <v-btn
              variant="text"
              @click.stop="openResponse(item)"
              color="#002060"
              size="small"
              class="mr-1"
            >
              {{ selectedQaType === 'pending' ? 'Answer' : 'View' }}
            </v-btn>



            <!-- Reopen button - only for completed questions -->
            <v-btn
              v-if="selectedQaType === 'completed'"
              variant="text"
              @click.stop="reopenQuestion(item)"
              color="warning"
              size="small"
              class="mr-1"
            >
              Reopen
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { db } from '../firebaseConfig';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  where
} from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';

export default {
  name: 'QaTable',
  components: { NotificationDialog },

  data() {
    return {
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      search: '',
      categoryFilter: null,

      // ⛔️ Removed: statusFilter/statusOptions
      assignedFilter: null, // ✅ new: filter by assignee (id | 'UNASSIGNED' | null)

      loading: false,
      questions: [],
      qaUsers: [],
      assigningId: null,       // row-level spinner id
      rowAssigneeMap: {},      // per-row model for the table autocomplete
      selectedQaType: this.$route.query.type || 'pending',
      qaTypeOptions: [
        { title: 'Questions Requiring Answer', value: 'pending' },
        { title: 'Completed Questions', value: 'completed' }
      ],
      headers: [
        { title: 'Question Reference', key: 'questionRef', sortable: true,  align: 'center' },
        { title: 'Category',   key: 'category',   sortable: true,  align: 'center' },
        { title: 'Question',   key: 'question',   sortable: true,  align: 'center' },
        { title: 'Status',     key: 'status',     sortable: true,  align: 'center' },
        { title: 'Date Added', key: 'dateAdded',  sortable: true,  align: 'center' },
        { title: 'Assigned To',key: 'assignedTo', sortable: false, align: 'center' },
        { title: 'Actions',    key: 'action',     sortable: false, align: 'center' }
      ],
      completedHeaders: [
        { title: 'Question Reference', key: 'questionRef',    sortable: true,  align: 'center' },
        { title: 'Client Name',     key: 'clientName',     sortable: true,  align: 'center' },
        { title: 'Client Surname',  key: 'clientSurname',  sortable: true,  align: 'center' },
        { title: 'Date Answered',   key: 'dateAnswered',   sortable: true,  align: 'center' },
        { title: 'Question',        key: 'question',       sortable: true,  align: 'center' },
        { title: 'Category',        key: 'category',       sortable: true,  align: 'center' },
        { title: 'Actions',         key: 'action',         sortable: false, align: 'center' }
      ],
      categoryOptions: [
        { title: 'All Categories', value: null },
        { title: 'Marriage', value: 'marriage' },
        { title: 'Divorce', value: 'divorce' },
        { title: 'Property', value: 'property' },
        { title: 'Contract', value: 'contract' },
        { title: 'Criminal', value: 'criminal' },
        { title: 'Corporate', value: 'corporate' }
      ],
      sortBy: ['question'],
      sortDesc: [false]
    };
  },

  computed: {
    ...mapGetters(['user', 'isAdmin', 'isManager', 'isEmployee', 'userRole']),

    // Items for autocompletes (table-level & filter-level)
    qaUserOptions() {
      return this.qaUsers.map((u) => {
        const first = (u.firstName || u.givenName || u.name || '').toString().trim();
        const last = (u.surname || u.lastName || '').toString().trim();
        const email = (u.email || '').toString().trim();
        const full = [first, last].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
        return {
          id: u.uid || u.id || email || this.cryptoRandomId(),
          firstName: first,
          lastName: last,
          email,
          fullName: full || email, // fallback if no name present
          role: u.role,
          displayName: full || email,
          searchBlob: `${first.toLowerCase()} ${last.toLowerCase()} ${email.toLowerCase()}`
        };
      });
    },

    // Options for the "Filter by Assignee"
    assignedFilterOptions() {
      const base = [
        { title: 'All Assignees', value: null },
        { title: 'Unassigned', value: 'UNASSIGNED' }
      ];
      const users = this.qaUserOptions.map(o => ({
        title: o.fullName || 'Unknown',
        value: o.id
      }));
      return [...base, ...users];
    },

    filteredQuestions() {
      const lower = (t) => t?.toString().toLowerCase() || '';

      return this.questions
        .filter((q) => {
          // Filter by QA type (pending vs completed)
          const matchesQaType =
            this.selectedQaType === 'pending'
              ? (q.status === 'pending' || q.status === 'in_progress')
              : q.status === 'completed';

          // QA Agents only see their assigned questions
          let matchesUserRole = true;
          if (this.userRole === 'QA Agent') {
            const meUid = this.user?.uid;
            const meName = this.user?.displayName || this.user?.email?.split('@')[0];
            const assignedName = this.getAssignedToDisplayValue(q);
            const assignedUid = q.assignedToUid || null;
            matchesUserRole = (assignedUid && meUid && assignedUid === meUid) || assignedName === meName;
          }

          // Search across key fields
          const assignedDisplay = this.getAssignedToDisplayValue(q);
          const matchesSearch = this.search
            ? [q.question, q.category, assignedDisplay, q.status]
                .some((field) => lower(field).includes(this.search.toLowerCase()))
            : true;

          // Category filter
          const matchesCategory = this.categoryFilter ? q.category === this.categoryFilter : true;

          // Assigned filter
          const matchesAssignee = this.matchesAssignedFilter(q);

          return matchesQaType && matchesUserRole && matchesSearch && matchesCategory && matchesAssignee;
        })
        // keep newest at top if your strings are date-like; ideally store real timestamps
        .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '') * -1);
    },

    currentQaTypeTitle() {
      const selected = this.qaTypeOptions.find((option) => option.value === this.selectedQaType);
      return selected ? selected.title : 'QA Management';
    }
  },

  methods: {
    navigateToCompletedQuestions() {
      this.$router.push({ name: 'CompletedQuestions' });
    },

    // Helper: does a question match current assignedFilter?
    matchesAssignedFilter(q) {
      // No filter -> all match
      if (this.assignedFilter === null || typeof this.assignedFilter === 'undefined') return true;

      // Unassigned sentinel
      if (this.assignedFilter === 'UNASSIGNED') {
        const hasStringAssigned = typeof q.assignedTo === 'string' && q.assignedTo.trim().length > 0;
        const hasObjAssigned = q.assignedTo && typeof q.assignedTo === 'object'
          && (q.assignedTo.displayName || q.assignedTo.email);
        const hasNormalized =
          (q.assignedToUid && String(q.assignedToUid).trim().length > 0) ||
          (q.assignedToName && String(q.assignedToName).trim().length > 0) ||
          (q.assignedToEmail && String(q.assignedToEmail).trim().length > 0);
        return !(hasStringAssigned || hasObjAssigned || hasNormalized);
      }

      // Specific user id selected
      // Direct UID match
      if (q.assignedToUid && String(q.assignedToUid) === String(this.assignedFilter)) return true;

      // Try to resolve selected user option to compare by name/email (for older docs)
      const opt = this.qaUserOptions.find(o => String(o.id) === String(this.assignedFilter));
      if (!opt) return false;

      // Compare normalized fields (newer docs)
      if (q.assignedToEmail && opt.email && q.assignedToEmail.toLowerCase() === opt.email.toLowerCase()) return true;
      if (q.assignedToName && opt.fullName && q.assignedToName.toLowerCase() === opt.fullName.toLowerCase()) return true;

      // Compare legacy assignedTo object or string
      if (typeof q.assignedTo === 'string') {
        const s = q.assignedTo.toLowerCase();
        if (opt.email && s.includes(opt.email.toLowerCase())) return true;
        if (opt.fullName && s.includes(opt.fullName.toLowerCase())) return true;
      } else if (q.assignedTo && typeof q.assignedTo === 'object') {
        if (q.assignedTo.email && opt.email && q.assignedTo.email.toLowerCase() === opt.email.toLowerCase()) return true;
        const disp = (q.assignedTo.displayName || '').toLowerCase();
        if (opt.fullName && disp === opt.fullName.toLowerCase()) return true;
      }

      return false;
    },

    async addSampleQuestions() {
      const sampleQuestions = [
        {
          category: 'marriage',
          question: 'What are the grounds for annulment in South African law?',
          assignedToName: 'Michiel Mostert',
          assignedToUid: null,
          assignedToEmail: 'mostertmichielc@gmail.com',
          status: 'pending',
          dateAdded: '06/18/2025',
          clientName: 'John',
          clientSurname: 'Smith',
          clientEmail: 'john.smith@example.com',
          dateReceived: '06/18/2025',
          dateAnswered: null,
          questionRef: 'QA-001',
          createdAt: new Date().toISOString()
        },
        {
          category: 'marriage',
          question: 'How to register a customary marriage?',
          assignedToName: 'Jane Doe',
          assignedToUid: null,
          assignedToEmail: 'jane.doe@example.com',
          status: 'in_progress',
          dateAdded: '06/17/2025',
          clientName: 'Sarah',
          clientSurname: 'Johnson',
          clientEmail: 'sarah.johnson@example.com',
          dateReceived: '06/17/2025',
          dateAnswered: null,
          questionRef: 'QA-002',
          createdAt: new Date().toISOString()
        },
        {
          category: 'property',
          question: 'Rights to property on divorce?',
          assignedToName: 'Michiel Mostert',
          assignedToUid: null,
          assignedToEmail: 'mostertmichielc@gmail.com',
          status: 'completed',
          dateAdded: '06/16/2025',
          clientName: 'Mike',
          clientSurname: 'Wilson',
          clientEmail: 'mike.wilson@example.com',
          dateReceived: '06/16/2025',
          dateAnswered: '06/20/2025',
          questionRef: 'QA-003',
          createdAt: new Date().toISOString()
        },
        {
          category: 'contract',
          question: 'What constitutes a valid employment contract?',
          assignedToName: 'Legal Team',
          assignedToUid: null,
          assignedToEmail: 'hr@abc-company.com',
          status: 'completed',
          dateAdded: '06/15/2025',
          clientName: 'ABC',
          clientSurname: 'Company',
          clientEmail: 'hr@abc-company.com',
          dateReceived: '06/15/2025',
          dateAnswered: '06/19/2025',
          questionRef: 'QA-004',
          createdAt: new Date().toISOString()
        }
      ];

      try {
        for (const question of sampleQuestions) {
          await addDoc(collection(db, 'questions'), question);
          const createdBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'System';
          await ActivityLogger.questionCreated(createdBy, this.user?.uid, question.question);
        }
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Sample questions added to Firebase successfully!';
        this.showNotification = true;
      } catch (error) {
        console.error('Error adding sample questions:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to add sample questions to Firebase.';
        this.showNotification = true;
      }
    },

    getQuestions() {
      this.loading = true;
      if (this.unsubscribeQuestions) this.unsubscribeQuestions();

      // If you have a timestamp field use that in orderBy; falling back to dateAdded string works, but timestamp is ideal.
      const questionsQuery = query(collection(db, 'questions'), orderBy('dateAdded', 'desc'));
      this.unsubscribeQuestions = onSnapshot(
        questionsQuery,
        (snapshot) => {
          this.questions = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

          // Hydrate the per-row assignee model so the chip shows current assignee
          const map = {};
          for (const q of this.questions) {
            const uid = q.assignedToUid || null;
            const match = uid ? this.qaUsers.find((u) => u.uid === uid) : null;
            if (match) {
              const first = (match.firstName || match.givenName || match.name || '').toString().trim();
              const last = (match.surname || match.lastName || '').toString().trim();
              const email = (match.email || '').toString().trim();
              const full = [first, last].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
              map[q.id] = {
                id: match.uid || match.id || email || this.cryptoRandomId(),
                firstName: first,
                lastName: last,
                email,
                fullName: full || email,
                role: match.role,
                displayName: full || email,
                searchBlob: `${first.toLowerCase()} ${last.toLowerCase()} ${email.toLowerCase()}`
              };
            } else {
              map[q.id] = null;
            }
          }
          this.rowAssigneeMap = map;

          if (this.questions.length === 0) {
            this.addSampleQuestions();
          }
          this.loading = false;
        },
        (error) => {
          console.error('Firestore error:', error);
          this.loading = false;
        }
      );
    },

    async loadQaUsers() {
      try {
        if (this.unsubscribeQaUsers) this.unsubscribeQaUsers();

        const qaUsersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'QA Agent'),
          where('status', '==', 'active')
        );

        this.unsubscribeQaUsers = onSnapshot(
          qaUsersQuery,
          (snapshot) => {
            this.qaUsers = snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));

            // Refresh row assignee bindings when users list updates (e.g., new QA Agent added)
            const refreshed = { ...this.rowAssigneeMap };
            for (const q of this.questions) {
              if (!q.id) continue;
              const uid = q.assignedToUid || null;
              const match = uid ? this.qaUsers.find((u) => u.uid === uid) : null;
              if (match) {
                const first = (match.firstName || match.givenName || match.name || '').toString().trim();
                const last = (match.surname || match.lastName || '').toString().trim();
                const email = (match.email || '').toString().trim();
                const full = [first, last].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
                refreshed[q.id] = {
                  id: match.uid || match.id || email || this.cryptoRandomId(),
                  firstName: first,
                  lastName: last,
                  email,
                  fullName: full || email,
                  role: match.role,
                  displayName: full || email,
                  searchBlob: `${first.toLowerCase()} ${last.toLowerCase()} ${email.toLowerCase()}`
                };
              } else {
                refreshed[q.id] = null;
              }
            }
            this.rowAssigneeMap = refreshed;
          },
          (error) => {
            console.error('Error loading QA users:', error);
          }
        );
      } catch (error) {
        console.error('Error setting up QA users listener:', error);
      }
    },

    async handleAssignedToChange(item, selectedObject) {
      // When cleared
      if (!selectedObject) {
        await this.assignQuestionToUser(item, null);
        return;
      }
      await this.assignQuestionToUser(item, selectedObject);
    },

    // Very small random id fallback when no id/uid/email is present
    cryptoRandomId() {
      try {
        return Array.from(crypto.getRandomValues(new Uint8Array(8)))
          .map(b => b.toString(16).padStart(2, '0')).join('');
      } catch {
        return Math.random().toString(16).slice(2);
      }
    },

    async assignQuestionToUser(item, selected) {
      try {
        this.assigningId = item.id;

        // Build assignment payload (or clear)
        const payload = selected
          ? {
              assignedToName: selected.fullName || selected.email || 'Unknown',
              assignedToUid: selected.id || null,
              assignedToEmail: selected.email || null
            }
          : {
              assignedToName: null,
              assignedToUid: null,
              assignedToEmail: null
            };

        // Persist to Firestore
        await updateDoc(doc(db, 'questions', item.id), {
          ...payload,
          updatedAt: new Date().toISOString()
        });

        // Also reflect on local item for immediate UI
        item.assignedToName = payload.assignedToName;
        item.assignedToUid = payload.assignedToUid;
        item.assignedToEmail = payload.assignedToEmail;

        // Log
        const assignedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        const questionTitle = (item.question || '').substring(0, 50) + ((item.question || '').length > 50 ? '...' : '');

        await ActivityLogger.questionAssigned(
          assignedBy,
          this.user?.uid,
          questionTitle,
          payload.assignedToName || 'Unassigned',
          'QA Table Assignment'
        );

        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = payload.assignedToName
          ? `Question assigned to ${payload.assignedToName} successfully!`
          : 'Assignment cleared successfully!';
        this.showNotification = true;
      } catch (error) {
        console.error('Error assigning question:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to assign question. Please try again.';
        this.showNotification = true;
      } finally {
        this.assigningId = null;
      }
    },

    customUserFilter(item, queryText, itemText) {
      const q = (queryText || '').toLowerCase();
      return (
        (item.fullName || '').toLowerCase().includes(q) ||
        (item.email || '').toLowerCase().includes(q)
      );
    },

    getAssignedToDisplayValue(item) {
      // Prefer normalized fields (new)
      if (item.assignedToName) return item.assignedToName;
      if (item.assignedToEmail) return item.assignedToEmail;

      // Backward compat: if older docs had string 'assignedTo'
      if (typeof item.assignedTo === 'string') return item.assignedTo;

      // If object (older code path)
      if (item.assignedTo && typeof item.assignedTo === 'object') {
        return item.assignedTo.displayName || item.assignedTo.email || 'Unassigned';
      }
      return 'Unassigned';
    },

    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success'
      };
      return colors[status] || 'warning';
    },

    async toggleQuestionStatus(item) {
      try {
        const newStatus =
          item.status === 'completed'
            ? 'pending'
            : item.status === 'pending'
            ? 'in_progress'
            : 'completed';

        if (!item.id) throw new Error('Question ID is missing');

        await updateDoc(doc(db, 'questions', item.id), {
          status: newStatus,
          updatedAt: new Date().toISOString()
        });

        const updatedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.questionUpdated(updatedBy, this.user?.uid, item.question, `status changed to ${newStatus}`);

        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = `Question status updated to ${newStatus.replace('_', ' ')} successfully!`;
        this.showNotification = true;
      } catch (error) {
        console.error('Error updating question status:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to update question status. Please try again.';
        this.showNotification = true;
      }
    },

    onQaTypeChange() {
      this.$router.push({ path: '/qa', query: { type: this.selectedQaType } });
      this.debouncedGetQuestions();
    },

    async reopenQuestion(item) {
      try {
        if (!item.id) throw new Error('Question ID is missing');

        await updateDoc(doc(db, 'questions', item.id), {
          status: 'pending',
          updatedAt: new Date().toISOString()
        });

        const reopenedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.questionUpdated(reopenedBy, this.user?.uid, item.question, 'reopened');

        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Question reopened successfully!';
        this.showNotification = true;
      } catch (error) {
        console.error('Error reopening question:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to reopen question. Please try again.';
        this.showNotification = true;
      }
    },

    async deleteQuestion(item) {
      if (confirm(`Are you sure you want to delete this question: "${item.question}"? This action cannot be undone.`)) {
        try {
          if (!item.id) throw new Error('Question ID is missing');

          await deleteDoc(doc(db, 'questions', item.id));

          const deletedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.questionDeleted(deletedBy, this.user?.uid, item.question);

          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = 'Question deleted successfully!';
          this.showNotification = true;
        } catch (error) {
          console.error('Error deleting question:', error);
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to delete question. Please try again.';
          this.showNotification = true;
        }
      }
    },

    debouncedGetQuestions: debounce(function () {
      this.getQuestions();
    }, 300),

    openResponse(item) {
      this.$router.push({ name: 'QaDetails', params: { id: item.id } });
    },

    viewMoreDetails(item) {
      this.$router.push({
        name: 'QaDetails',
        params: { id: item.id },
        query: { viewDetails: 'true' }
      });
    }
  },

  watch: {
    selectedQaType() {
      this.debouncedGetQuestions();
    },
    '$route.query.type'(newType) {
      if (newType && newType !== this.selectedQaType) {
        this.selectedQaType = newType;
      }
    }
  },

  created() {
    this.loadQaUsers();
    this.getQuestions();
  },

  beforeUnmount() {
    if (this.unsubscribeQuestions) this.unsubscribeQuestions();
    if (this.unsubscribeQaUsers) this.unsubscribeQaUsers();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.custom-header {
  background-color: #e8e8e8;
}
</style>
